import { escapeHtml, encodeFilePath } from '../../../../modules/files/file-list.utils.js';
import { hideAudioPlayerToggle, initAudioPlayerToggle } from './toggle.js';
import { setupPlayerUI, clearMediaSession, setupMediaSession } from './player-ui.js';
import { loadSavedState } from './player-state.js';
import { 
	isAudioLecture, 
	extractLectureName, 
	initTranscriptContainer, 
	loadAndDisplayTranscript, 
	clearTranscript 
} from './transcript.js';

// Audio player state
let audioPlayerElement = null;
let audioPlayerTimeout = null;
let currentAudioFile = null;
let audioPlayerHovered = false;
let audioIconHovered = false;
let audioPlayerCollapsed = false;
let savedPlayerVolume = 1.0; // Store volume between track changes

// Audio player configuration
export const AUDIO_PLAYER_POSITION_COOKIE_NAME = 'audio_player_position';
export const AUDIO_PLAYER_WIDTH = 420;
export const AUDIO_PLAYER_HEIGHT = 140; // Base height, will expand with transcript
export const AUDIO_PLAYER_HEIGHT_WITH_TRANSCRIPT = 280; // Height when transcript is visible

// Cookie helpers for audio player position
function saveAudioPlayerPosition(left, top) {
	try {
		const position = JSON.stringify({ left, top });
		const expires = new Date();
		expires.setFullYear(expires.getFullYear() + 1);
		document.cookie = `${AUDIO_PLAYER_POSITION_COOKIE_NAME}=${encodeURIComponent(position)}; expires=${expires.toUTCString()}; path=/`;
	} catch (e) {
		console.warn('[AudioPlayer] Failed to save position to cookie:', e);
	}
}

function loadAudioPlayerPosition() {
	try {
		const cookies = document.cookie.split(';');
		for (let cookie of cookies) {
			cookie = cookie.trim();
			if (cookie.startsWith(AUDIO_PLAYER_POSITION_COOKIE_NAME + '=')) {
				const value = decodeURIComponent(cookie.substring(AUDIO_PLAYER_POSITION_COOKIE_NAME.length + 1));
				const position = JSON.parse(value);
				if (position && typeof position.left === 'number' && typeof position.top === 'number') {
					return position;
				}
			}
		}
	} catch (e) {
		console.warn('[AudioPlayer] Failed to load position from cookie:', e);
	}
	return null;
}

export function fadeInVolume(audioEl, targetVolume, duration = 4000) {
	if (!audioEl) return;
	
	// Use targetVolume parameter, or default to 1.0 if not provided
	const finalVolume = targetVolume !== undefined && targetVolume >= 0 ? targetVolume : 1.0;
	
	// Set initial volume to 0
	audioEl.volume = 0;
	
	// Calculate steps for smooth fade-in
	const steps = 60; // 60 steps for smooth animation
	const stepDuration = duration / steps;
	const volumeStep = finalVolume / steps;
	
	let currentStep = 0;
	
	const fadeInterval = setInterval(() => {
		currentStep++;
		const newVolume = Math.min(finalVolume, volumeStep * currentStep);
		audioEl.volume = newVolume;
		
		if (currentStep >= steps || newVolume >= finalVolume) {
			audioEl.volume = finalVolume;
			clearInterval(fadeInterval);
		}
	}, stepDuration);
}

export function updateAudioPlayerTrack(file, fileKey) {
	// Check if player element exists in DOM (might have been removed when closed)
	const existsInDOM = audioPlayerElement && audioPlayerElement.length && 
		document.body.contains(audioPlayerElement[0]);
	
	if (!existsInDOM) {
		// Player doesn't exist or was removed - create it
		// This will also clear the "closed" flag
		showAudioPlayer(file, fileKey);
		return;
	}
	
	const fileName = file.n || file.name || 'Unknown';
	const baseUrl = window.location.origin;
	
	let audioUrl = '';
	// Use server-provided _url if available (includes version)
	if (file._url) {
		audioUrl = `${baseUrl}${file._url}`;
	} else {
		// Fallback to old logic if _url not provided
		const isOT = !!(file.ot || file.ot_level || (typeof file.category === 'string' && file.category.toUpperCase() === 'OT'));
		const p = (file.p || file.path || '').replace(/^\//, '').replace(/\/+/, '/');
		const n = (file.n || file.name || '');
		const rel = `${p}${p ? '/' : ''}${n}`;
		if (isOT) {
			audioUrl = `${baseUrl}/library/${encodeFilePath(rel)}?ot=1`;
		} else {
			audioUrl = `${baseUrl}/library/${encodeFilePath(rel)}`;
		}
	}
	
	// Update title
	audioPlayerElement.find('.audio-player__title').text(fileName);
	
	// Update audio source
	const audio = audioPlayerElement.find('audio');
	if (audio.length > 0) {
		const audioEl = audio[0];
		const source = audio.find('source');
		
		// Save current volume BEFORE updating source (to preserve user's volume setting)
		// Get volume from audio element, or use saved volume, or use default 1.0
		const currentVolume = audioEl.volume !== undefined && audioEl.volume >= 0 ? audioEl.volume : savedPlayerVolume;
		savedPlayerVolume = currentVolume; // Update saved volume
		
		if (source.length > 0) {
			source.attr('src', audioUrl);
			audioEl.load();
		} else {
			audio.attr('src', audioUrl);
			audioEl.load();
		}
		
		// Check if this is an audio lecture and load transcript
		if (isAudioLecture(file)) {
			const lectureName = extractLectureName(file);
			if (lectureName) {
				loadAndDisplayTranscript(lectureName, 'en').catch(err => {
					console.error('[AudioPlayer] Failed to load transcript:', err);
				});
			}
		} else {
			clearTranscript();
		}
		
		// Setup autoplay with fade-in when new track is loaded
		// Check if this is a new track (different from current or saved state)
		const savedState = loadSavedState();
		const isNewTrack = !currentAudioFile || currentAudioFile.fileKey !== fileKey || 
			!savedState.track || savedState.track.fileKey !== fileKey;
		
		// Listen for canplay event to start playback
		const onCanPlay = function() {
			audioEl.removeEventListener('canplay', onCanPlay);
			
			// If this is a new track, autoplay with fade-in
			if (isNewTrack) {
				// Get current volume from audio element (user's current volume setting)
				// This is the volume that user has set in the player controls
				const currentVolume = audioEl.volume !== undefined && audioEl.volume > 0 ? audioEl.volume : savedPlayerVolume;
				audioEl.play().then(() => {
					// Apply fade-in using current volume from element (user's volume setting)
					fadeInVolume(audioEl, currentVolume, 4000);
				}).catch(err => {
					console.warn('[AudioPlayer] Auto-play failed (user interaction may be required):', err);
				});
			} else {
				// Same track - restore position if available
				if (savedState.position && savedState.position > 1) {
					audioEl.currentTime = savedState.position;
				}
			}
		};
		audioEl.addEventListener('canplay', onCanPlay, { once: true });
		
		// Calculate filePath for Media Session
		const p = (file.p || file.path || '').replace(/^\//, '').replace(/\/+/, '/');
		const n = (file.n || file.name || '');
		const filePath = `${p}${p ? '/' : ''}${n}`;
		
		// Update Media Session metadata
		setupMediaSession(audioEl, fileName, filePath);
		
		// Track volume changes to save user's volume setting
		audioEl.addEventListener('volumechange', function() {
			if (audioEl.volume !== undefined && audioEl.volume >= 0) {
				savedPlayerVolume = audioEl.volume;
			}
		});
	}
	
	currentAudioFile = { file, fileKey };
}

export async function restoreAudioPlayerState() {
	const savedState = loadSavedState();
	if (!savedState.track || !savedState.track.fileKey) {
		return;
	}
	
	// Try to find file in filesStore
	let file = null;
	try {
		if (window.filesStore && typeof window.filesStore.getFiles === 'function') {
			const allFiles = window.filesStore.getFiles();
			file = allFiles.find(f => {
				// Try to match by fileKey (if available) or by path+name
				if (f.key && f.key === savedState.track.fileKey) {
					return true;
				}
				const filePath = f.p || f.path || '';
				const fileName = f.n || f.name || '';
				return filePath === savedState.track.path && fileName === savedState.track.name;
			});
		}
	} catch (e) {
		console.warn('[AudioPlayer] Error finding file:', e);
	}
	
	if (!file) {
		return;
	}
	
	// Get fileKey - try to use saved fileKey or generate from file
	const fileKey = file.key || savedState.track.fileKey || `${file.p || file.path || ''}/${file.n || file.name || ''}`;
	
	// Create player with saved track
	showAudioPlayer(file, fileKey);
	
	// Wait a bit for player to be created and audio element to be ready
	setTimeout(() => {
		const audioPlayerElement = getAudioPlayerElement();
		if (!audioPlayerElement || !audioPlayerElement.length) {
			console.warn('[AudioPlayer] ⚠️ Player element not found after creation');
			return;
		}
		
		const audio = audioPlayerElement.find('audio');
		if (audio.length === 0 || !audio[0]) {
			console.warn('[AudioPlayer] ⚠️ Audio element not found');
			return;
		}
		
		const audioEl = audio[0];
		
		// Restore position when audio is ready
		const onCanPlay = function() {
			audioEl.removeEventListener('canplay', onCanPlay);
			
			// Restore position if available
			if (savedState.position && savedState.position > 1) {
				audioEl.currentTime = savedState.position;
				
			// Transcript is now handled via language links only
			}
			
			// Restore collapsed state if saved
			if (savedState.collapsed) {
				setAudioPlayerCollapsed(true);
				// Import animatePlayerCollapse dynamically to avoid circular dependency
				import('./player-ui.js').then(({ animatePlayerCollapse }) => {
					animatePlayerCollapse(true, null);
				}).catch(err => {
					console.warn('[AudioPlayer] Failed to import animatePlayerCollapse:', err);
				});
			}
		};
		
		// If audio is already ready, call handler immediately
		if (audioEl.readyState >= 2) { // HAVE_CURRENT_DATA or higher
			onCanPlay();
		} else {
			audioEl.addEventListener('canplay', onCanPlay, { once: true });
		}
	}, 100);
}

export function hideAudioPlayer() {
	if (audioPlayerTimeout) {
		clearTimeout(audioPlayerTimeout);
		audioPlayerTimeout = null;
	}
	$(document).off('mousemove.audio-player-drag mouseup.audio-player-drag');
	audioPlayerHovered = false;
	audioIconHovered = false;
	audioPlayerCollapsed = false;
	// Clear current audio file to prevent toggle button from showing
	currentAudioFile = null;
	if (audioPlayerElement) {
		const rect = audioPlayerElement[0].getBoundingClientRect();
		saveAudioPlayerPosition(rect.left, rect.top);
		audioPlayerElement.removeClass('audio-player-overlay--visible');
		// Clear Media Session
		clearMediaSession();
		// Clear transcript
		clearTranscript();
		// Hide toggle button FIRST before removing player
		hideAudioPlayerToggle();
		// Cleanup state persistence (import dynamically to avoid circular dependency)
		import('./player-state.js').then(({ cleanupPlayerStatePersistence }) => {
			cleanupPlayerStatePersistence();
		}).catch(err => {
			console.warn('[AudioPlayer] Failed to cleanup state persistence:', err);
		});
		// Remove element immediately and clear reference
		const elementToRemove = audioPlayerElement;
		audioPlayerElement = null; // Clear reference immediately to prevent updateToggleButton from showing button
		setTimeout(() => {
			if (elementToRemove && elementToRemove.length) {
				elementToRemove.remove();
			}
			// Ensure toggle button is hidden after player removal
			hideAudioPlayerToggle();
		}, 200);
	} else {
		// If player element doesn't exist, still ensure toggle is hidden
		currentAudioFile = null;
		hideAudioPlayerToggle();
	}
}

export function showAudioPlayer(file, fileKey) {
	if (window.innerWidth < 1200) return;
	
	// Clear "closed" flag when player is opened (user wants to use it again)
	try {
		const expires = new Date(0); // Expire immediately
		document.cookie = `audio_player_closed=; expires=${expires.toUTCString()}; path=/`;
	} catch (e) {
		console.warn('[AudioPlayer] Failed to clear closed flag:', e);
	}
	
	const fileName = file.n || file.name || 'Unknown';
	const baseUrl = window.location.origin;
	
	let audioUrl = '';
	// Use server-provided _url if available (includes version)
	if (file._url) {
		audioUrl = `${baseUrl}${file._url}`;
	} else {
		// Fallback to old logic if _url not provided
		const isOT = !!(file.ot || file.ot_level || (typeof file.category === 'string' && file.category.toUpperCase() === 'OT'));
		const p = (file.p || file.path || '').replace(/^\//, '').replace(/\/+/, '/');
		const n = (file.n || file.name || '');
		const rel = `${p}${p ? '/' : ''}${n}`;
		if (isOT) {
			audioUrl = `${baseUrl}/library/${encodeFilePath(rel)}?ot=1`;
		} else {
			audioUrl = `${baseUrl}/library/${encodeFilePath(rel)}`;
		}
	}
	
	// If player already exists, just update track
	if (audioPlayerElement && audioPlayerElement.length) {
		// Check if element still exists in DOM (might have been removed when closed)
		const existsInDOM = document.body.contains(audioPlayerElement[0]);
		if (existsInDOM) {
			updateAudioPlayerTrack(file, fileKey);
			return;
		} else {
			// Element was removed from DOM (player was closed) - reset reference
			audioPlayerElement = null;
		}
	}
	
	// Save volume from existing player before creating new one (if exists)
	if (audioPlayerElement && audioPlayerElement.length) {
		const existingAudio = audioPlayerElement.find('audio');
		if (existingAudio.length > 0 && existingAudio[0]) {
			const existingVolume = existingAudio[0].volume;
			if (existingVolume !== undefined && existingVolume >= 0) {
				savedPlayerVolume = existingVolume;
			}
		}
	}
	
	const playerHtml = `
		<div class="audio-player-overlay" id="audio-player-overlay">
			<div class="audio-player-container">
				<div class="audio-player__header">
					<div class="audio-player__title">${escapeHtml(fileName)}</div>
					<button class="audio-player__minimize" aria-label="Minimize" title="Minimize">
						<i class="bi bi-dash-lg"></i>
					</button>
					<button class="audio-player__close" aria-label="Close">
						<i class="bi bi-x-lg"></i>
					</button>
				</div>
				<div class="audio-player__content">
					<audio controls class="audio-player__audio" preload="metadata">
						<source src="${escapeHtml(audioUrl)}" type="audio/mpeg">
						Your browser does not support the audio element.
					</audio>
				</div>
			</div>
		</div>
	`;
	
	$('body').append(playerHtml);
	audioPlayerElement = $('#audio-player-overlay');
	currentAudioFile = { file, fileKey };
	
	// Set saved volume on new audio element
	const audio = audioPlayerElement.find('audio');
	if (audio.length > 0 && audio[0]) {
		audio[0].volume = savedPlayerVolume;
	}
	
	// Position player
	const savedPosition = loadAudioPlayerPosition();
	let left, top;
	
	if (savedPosition) {
		const maxLeft = window.innerWidth - AUDIO_PLAYER_WIDTH;
		const maxTop = window.innerHeight - AUDIO_PLAYER_HEIGHT_WITH_TRANSCRIPT; // Use max height for positioning
		left = Math.max(0, Math.min(savedPosition.left, maxLeft));
		top = Math.max(0, Math.min(savedPosition.top, maxTop));
	} else {
		// Default position: bottom-right corner
		left = window.innerWidth - AUDIO_PLAYER_WIDTH - 20;
		top = window.innerHeight - AUDIO_PLAYER_HEIGHT_WITH_TRANSCRIPT - 20;
	}
	
	audioPlayerElement.css({
		position: 'fixed',
		left: left + 'px',
		top: top + 'px',
		width: AUDIO_PLAYER_WIDTH + 'px',
		height: 'auto' // Auto height to adapt to content
	});
	
	// Calculate filePath for UI setup
	const p = (file.p || file.path || '').replace(/^\//, '').replace(/\/+/, '/');
	const n = (file.n || file.name || '');
	const filePath = `${p}${p ? '/' : ''}${n}`;
	
	// Setup UI (drag&drop, event handlers, Media Session)
	setupPlayerUI(audioPlayerElement, fileName, filePath, audioUrl, saveAudioPlayerPosition);
	
	// Track volume changes on new audio element to save user's volume setting
	const audioForVolume = audioPlayerElement.find('audio');
	if (audioForVolume.length > 0 && audioForVolume[0]) {
		audioForVolume[0].addEventListener('volumechange', function() {
			if (this.volume !== undefined && this.volume >= 0) {
				savedPlayerVolume = this.volume;
			}
		});
	}
	
	// Initialize transcript container
	initTranscriptContainer(audioPlayerElement);
	
	// Check if this is an audio lecture and load transcript
	if (isAudioLecture(file)) {
		const lectureName = extractLectureName(file);
		if (lectureName) {
			loadAndDisplayTranscript(lectureName, 'en').then(() => {
				// Height is now auto, adapts to content automatically
			}).catch(err => {
				console.error('[AudioPlayer] Failed to load transcript:', err);
			});
		}
	} else {
		clearTranscript();
	}
	
	// Show with animation
	setTimeout(() => {
		audioPlayerElement.addClass('audio-player-overlay--visible');
		// Re-initialize toggle button to update state with correct paused check
		// This ensures paused state is checked correctly after player is shown
		setTimeout(() => {
			initAudioPlayerToggle();
		}, 50);
	}, 10);
}

// State getters/setters
export function getAudioPlayerState() {
	return {
		audioPlayerElement,
		audioPlayerTimeout,
		currentAudioFile,
		audioPlayerHovered,
		audioIconHovered,
		audioPlayerCollapsed
	};
}

export function setAudioPlayerState(state) {
	audioPlayerElement = state.audioPlayerElement;
	audioPlayerTimeout = state.audioPlayerTimeout;
	currentAudioFile = state.currentAudioFile;
	audioPlayerHovered = state.audioPlayerHovered;
	audioIconHovered = state.audioIconHovered;
	audioPlayerCollapsed = state.audioPlayerCollapsed || false;
}

// Export state accessors for toggle.js and player-ui.js
export function getAudioPlayerElement() {
	return audioPlayerElement;
}

export function getAudioPlayerCollapsed() {
	return audioPlayerCollapsed;
}

export function setAudioPlayerCollapsed(value) {
	audioPlayerCollapsed = value;
}

// Export saved volume getter/setter
export function getSavedPlayerVolume() {
	return savedPlayerVolume;
}

export function setSavedPlayerVolume(volume) {
	if (volume !== undefined && volume >= 0 && volume <= 1) {
		savedPlayerVolume = volume;
	}
}

export function getCurrentAudioFile() {
	return currentAudioFile;
}

// Export for transcript.js
if (typeof window !== 'undefined') {
	window.getCurrentAudioFile = getCurrentAudioFile;
}

export function savePlayerPosition(left, top) {
	saveAudioPlayerPosition(left, top);
}
